import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  Brain,
  Cloud,
  Zap,
  ArrowUpRight,
  ShieldCheck,
  BarChart3,
  GitBranch,
  Cpu,
  type LucideIcon,
} from 'lucide-react';

interface Service {
  icon: LucideIcon;
  label: string;
  tag: string;
  description: string;
  features: string[];
  accentColor: string;
  iconBg: string;
  size: 'large' | 'medium';
}

interface Highlight {
  icon: LucideIcon;
  label: string;
  desc: string;
}

const services: Service[] = [
  {
    icon: Brain,
    label: 'AI Consulting',
    tag: 'Intelligence Layer',
    description:
      'From LLM selection and fine-tuning to RAG pipelines and agent orchestration—we architect AI systems that deliver measurable ROI.',
    features: ['LLM Integration & Fine-tuning', 'Claude & GPT-4 Pipelines', 'RAG Architecture', 'AI Cost Optimization'],
    accentColor: 'from-blue-600/20 to-indigo-600/10',
    iconBg: 'bg-blue-600/15 text-blue-400',
    size: 'large',
  },
  {
    icon: Cloud,
    label: 'Cloud Infrastructure',
    tag: 'AWS Focus',
    description:
      'Production-grade AWS architecture with Terraform, EKS, and serverless patterns. Built for scale, security, and zero-downtime deployments.',
    features: ['AWS Architecture', 'Terraform IaC', 'EKS & Fargate', 'Cost Management'],
    accentColor: 'from-sky-600/15 to-blue-600/10',
    iconBg: 'bg-sky-500/15 text-sky-400',
    size: 'medium',
  },
  {
    icon: Zap,
    label: 'Workflow Automation',
    tag: 'Efficiency Engine',
    description:
      'End-to-end process automation connecting your tools, APIs, and AI systems into seamless pipelines that run 24/7.',
    features: ['API Integration', 'Event-Driven Pipelines', 'CI/CD Automation', 'Monitoring & Alerts'],
    accentColor: 'from-violet-600/15 to-purple-600/10',
    iconBg: 'bg-violet-500/15 text-violet-400',
    size: 'medium',
  },
];

const highlights: Highlight[] = [
  { icon: ShieldCheck, label: 'SOC 2 Ready', desc: 'Security-first architecture from day one' },
  { icon: BarChart3, label: 'Observable', desc: 'Full-stack telemetry with OpenTelemetry' },
  { icon: GitBranch, label: 'GitOps Native', desc: 'Everything as code, everything in git' },
  { icon: Cpu, label: 'AI-Augmented', desc: 'Every workflow enhanced with intelligence' },
];

interface ServiceCardProps {
  service: Service;
  index: number;
}

function ServiceCard({ service, index }: ServiceCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={`bento-card group relative overflow-hidden p-6 lg:p-8 flex flex-col gap-5 ${
        service.size === 'large' ? 'lg:col-span-2' : ''
      }`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${service.accentColor} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${service.iconBg}`}>
            <service.icon size={20} />
          </div>
          <span className="text-[11px] font-medium text-slate-500 border border-white/[0.06] px-2.5 py-1 rounded-full">
            {service.tag}
          </span>
        </div>

        <h3 className="text-xl font-bold text-white mb-2">{service.label}</h3>
        <p className="text-slate-400 text-sm leading-relaxed mb-5">{service.description}</p>

        <ul className="flex flex-wrap gap-2">
          {service.features.map((feat) => (
            <li
              key={feat}
              className="text-xs text-slate-400 border border-white/[0.07] px-2.5 py-1 rounded-md bg-white/[0.02]"
            >
              {feat}
            </li>
          ))}
        </ul>
      </div>

      <div className="relative z-10 mt-auto pt-2">
        <a
          href="#contact"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-400 hover:text-blue-300 transition-colors group/link"
        >
          Learn more
          <ArrowUpRight
            size={13}
            className="group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform duration-200"
          />
        </a>
      </div>
    </motion.div>
  );
}

export default function Services() {
  const headerRef = useRef<HTMLDivElement>(null);
  const headerInView = useInView(headerRef, { once: true, margin: '-60px' });

  return (
    <section id="services" className="relative py-24 lg:py-32 bg-black">
      <div className="divider-glow mb-0" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className="max-w-2xl mb-16">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-xs font-medium text-blue-400 uppercase tracking-widest mb-3"
          >
            What We Build
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="text-4xl sm:text-5xl font-black text-white tracking-tight leading-tight mb-4"
          >
            Engineering services
            <br />
            <span className="gradient-text">that move the needle.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.16 }}
            className="text-slate-400 text-base leading-relaxed"
          >
            Three core disciplines, one integrated team. We don't just build—we operationalize.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-12">
          {services.map((service, i) => (
            <ServiceCard key={service.label} service={service} index={i} />
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {highlights.map((h, i) => (
            <motion.div
              key={h.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bento-card p-4 flex items-start gap-3"
            >
              <div className="w-8 h-8 rounded-lg bg-blue-600/10 flex items-center justify-center text-blue-400 flex-shrink-0 mt-0.5">
                <h.icon size={15} />
              </div>
              <div>
                <div className="text-sm font-semibold text-white mb-0.5">{h.label}</div>
                <div className="text-xs text-slate-500 leading-relaxed">{h.desc}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
